/*
 * qi-checkout-wins.js — QI checkout quick wins #2, #3, #4  (checkout-ready PDPs only)
 * ---------------------------------------------------------------------------------
 * Win 2  Live order total     : qty x tier price + setup fee, itemised, before Add to Cart.
 * Win 3  Setup fee visible    : the one-time fee is a named line, not a cart surprise.
 * Win 4  Delivery promise     : "Order today -> in hands by <date>" + rush option.
 * Plus   Field counters       : - / + steppers around #quantity and #imprintColorId.
 *
 * Dependencies / contract
 *   - qi-live-price.js owns ALL tier maths and broadcasts `qi:price` on document.
 *     This module never re-derives a tier; it only renders what it is handed.
 *       detail = { available, qty, unit, lineTotal, setupFee, grandTotal, tierQty, currency }
 *     `available:false` is the contact-for-pricing sentinel (no numbers to show).
 *   - window.QI_DELIVERY = { days:<int>, rushDays:<int|null>, rushLink:<string> }
 *     emitted by product.liquid. `days` is the SAME value that drives the flatpickr
 *     minDate (#min_plus_date), and is counted in CALENDAR days, so the promised
 *     date can never contradict the earliest date the calendar lets you pick.
 *
 * Renders (all optional — a missing node is skipped, never thrown on):
 *   #qi-lp-tier        tier label, e.g. "Tier 36+ · $36.01 /ea."
 *   #qi-lp-line-label  "36 × $36.01"
 *   #qi-lp-setup-row   shown only while a setup fee actually applies
 *   #qi-lp-setup       the fee amount
 *   #qi-lp-grand       grand total
 *   #qi-w2-buy         Add-to-Cart button gets the total appended
 *   #qi-deliv-date     standard in-hands date
 *   #qi-deliv-rush     rush line (date, or a rush-request link)
 *
 * Coexistence: reads only ids it owns plus #min_plus_date; binds via addEventListener.
 */
(function () {
  'use strict';

  function el(id) { return document.getElementById(id); }

  function money(n, cur) {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: cur || 'USD'
      }).format(Number(n) || 0);
    } catch (e) {
      return '$' + (Number(n) || 0).toFixed(2);
    }
  }

  /* ---------------------------------------------------------------- wins 2+3 */

  var BUY_LABEL = null; // the button's original text, captured once

  function renderPrice(d) {
    var tierEl  = el('qi-lp-tier');
    var lineEl  = el('qi-lp-line-label');
    var feeRow  = el('qi-lp-setup-row');
    var feeEl   = el('qi-lp-setup');
    var rcRow   = el('qi-lp-runchg-row');
    var rcEl    = el('qi-lp-runchg');
    var rcNote  = el('qi-lp-runchg-note');
    var grandEl = el('qi-lp-grand');
    var buyBtn  = el('qi-w2-buy');

    // contact-for-pricing: blank the derived figures rather than showing stale ones
    if (!d || d.available === false) {
      if (tierEl) tierEl.textContent = '';
      if (lineEl) lineEl.textContent = '';
      if (feeRow) feeRow.style.display = 'none';
      if (rcRow) rcRow.style.display = 'none';
      if (grandEl) grandEl.textContent = '—';
      if (buyBtn && BUY_LABEL !== null) setBuyLabel(buyBtn, BUY_LABEL);
      return;
    }

    var cur = d.currency;

    if (tierEl) {
      tierEl.textContent = 'Tier ' + Number(d.tierQty).toLocaleString('en-US') +
        '+  ·  ' + money(d.unit, cur) + ' /ea.';
    }
    if (lineEl) {
      lineEl.textContent = Number(d.qty).toLocaleString('en-US') + ' × ' + money(d.unit, cur);
    }

    // Win 3: the fee is only real once an imprint method is chosen — mirror that
    // exactly, so the box never promises a charge the cart will not make.
    if (feeRow) feeRow.style.display = d.setupFee > 0 ? 'flex' : 'none';
    if (feeEl)  feeEl.textContent = money(d.setupFee, cur);

    // 'flex', never '': clearing the inline display drops the row back to the
    // stylesheet, which defines nothing for these rows, so they render block
    // and the amount stops right-aligning.
    // Run charge: shown only when one actually applies, for the same reason as
    // the setup fee -- the panel must never promise a charge the cart will not
    // make, nor hide one it will.
    if (rcRow) rcRow.style.display = d.runCharge > 0 ? 'flex' : 'none';
    if (rcEl)  rcEl.textContent = money(d.runCharge, cur);
    if (rcNote) {
      rcNote.textContent = (d.runCharge > 0)
        ? money(d.runChargeUnitPrice, cur) + '/pc × ' + d.runChargeUnits +
          (d.runChargeUnits === 1 ? ' run' : ' runs') + ' × ' + Number(d.qty).toLocaleString('en-US') + ' pcs'
        : '';
    }

    if (grandEl) grandEl.textContent = money(d.grandTotal, cur);

    if (buyBtn) {
      if (BUY_LABEL === null) BUY_LABEL = getBuyLabel(buyBtn);
      setBuyLabel(buyBtn, BUY_LABEL + ' — ' + money(d.grandTotal, cur));
    }
  }

  // the button holds an <span class="icon"> before its text; only touch the text node
  function textNodeOf(btn) {
    for (var i = btn.childNodes.length - 1; i >= 0; i--) {
      var n = btn.childNodes[i];
      if (n.nodeType === 3 && n.nodeValue.trim()) return n;
    }
    return null;
  }
  function getBuyLabel(btn) {
    var n = textNodeOf(btn);
    return n ? n.nodeValue.trim() : btn.textContent.trim();
  }
  function setBuyLabel(btn, text) {
    var n = textNodeOf(btn);
    if (n) n.nodeValue = ' ' + text; else btn.textContent = text;
  }

  /* ------------------------------------------------------------------ win 4 */

  var DAY_MS = 86400000;

  function addDays(base, n) {
    var d = new Date(base.getTime());
    d.setDate(d.getDate() + n);
    return d;
  }

  function fmt(d) {
    try {
      return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    } catch (e) {
      return (d.getMonth() + 1) + '/' + d.getDate();
    }
  }

  // Prefer the live #min_plus_date input (the very value flatpickr uses) and fall
  // back to the Liquid-rendered config, so the two can never drift apart.
  function leadDays() {
    var input = el('min_plus_date');
    var fromInput = input ? parseInt(input.value, 10) : NaN;
    if (isFinite(fromInput) && fromInput > 0) return fromInput;

    var cfg = window.QI_DELIVERY || {};
    var fromCfg = parseInt(cfg.days, 10);
    return (isFinite(fromCfg) && fromCfg > 0) ? fromCfg : 15;
  }

  function renderDelivery() {
    var cfg = window.QI_DELIVERY || {};
    var dateEl = el('qi-deliv-date');
    var rushEl = el('qi-deliv-rush');
    if (!dateEl && !rushEl) return;

    var today = new Date();
    if (dateEl) dateEl.textContent = fmt(addDays(today, leadDays()));

    if (!rushEl) return;
    var rushDays = parseInt(cfg.rushDays, 10);
    if (isFinite(rushDays) && rushDays > 0 && rushDays < leadDays()) {
      // this product genuinely carries the rush tag — quote the real date
      rushEl.innerHTML = '';
      rushEl.appendChild(document.createTextNode('Need it sooner? Rush service available — as fast as '));
      var b = document.createElement('b');
      b.textContent = fmt(addDays(today, rushDays));
      rushEl.appendChild(b);
      rushEl.style.display = '';
    } else if (cfg.rushLink) {
      // no rush tag: offer the existing rush-request route rather than invent a date
      rushEl.innerHTML = '';
      rushEl.appendChild(document.createTextNode('Need it sooner? '));
      var a = document.createElement('a');
      a.href = cfg.rushLink;
      a.rel = 'nofollow';   // quote-form URL: keep crawlers off it (AEO/GEO plan item 7)
      a.textContent = 'Ask us about rush service →';
      a.style.color = 'inherit';
      a.style.textDecoration = 'underline';
      rushEl.appendChild(a);
      rushEl.style.display = '';
    } else {
      rushEl.style.display = 'none';
    }
  }

  /* ------------------------------------ − / + counters (quantity + colours) */

  // Generic over both fields. Bounds come from the input's own min/max attributes:
  // quantity has a min (the MOQ) and NO max, colours are 1–4. A missing max means
  // no upper clamp — never assume one, or quantity would be capped at 4.
  // The stepper only ever writes through the input and re-fires input+change, so
  // qi-live-price.js, the tier-variant selector and onImprintChange() all keep
  // working untouched, and input.disabled stays authoritative.
  var COUNTERS = [
    { wrap: 'qi-qty-stepper', input: 'quantity' },
    { wrap: 'qi-colors-stepper', input: 'imprintColorId' }
  ];

  function bounds(input) {
    var min = parseInt(input.getAttribute('min'), 10);
    var max = parseInt(input.getAttribute('max'), 10);
    return { min: isFinite(min) ? min : 1, max: isFinite(max) ? max : null };
  }

  function stepField(inputId, wrapId, delta) {
    var input = el(inputId);
    if (!input || input.disabled) return;
    var b = bounds(input);
    var cur = parseInt(input.value, 10);
    if (!isFinite(cur)) cur = b.min;
    var next = cur + delta;
    if (next < b.min) next = b.min;
    if (b.max !== null && next > b.max) next = b.max;
    if (next === cur) { syncCounter(inputId, wrapId); return; }
    input.value = next;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
    // legacy helper that keeps the submitted variant in step with the quantity
    if (inputId === 'quantity' && typeof window.setTierVariantIdByQty === 'function') {
      try { window.setTierVariantIdByQty(); } catch (e) { /* absent on some templates */ }
    }
    syncCounter(inputId, wrapId);
  }

  function syncCounter(inputId, wrapId) {
    var input = el(inputId);
    var wrap = el(wrapId);
    if (!input || !wrap) return;
    var dec = wrap.querySelector('.qi-num-dec');
    var inc = wrap.querySelector('.qi-num-inc');
    var b = bounds(input);
    var cur = parseInt(input.value, 10);
    var off = input.disabled;
    wrap.classList.toggle('is-disabled', off);
    if (dec) dec.disabled = off || !isFinite(cur) || cur <= b.min;
    if (inc) inc.disabled = off || (b.max !== null && isFinite(cur) && cur >= b.max);
  }

  function syncAllCounters() {
    for (var i = 0; i < COUNTERS.length; i++) syncCounter(COUNTERS[i].input, COUNTERS[i].wrap);
  }

  function bindCounters() {
    for (var i = 0; i < COUNTERS.length; i++) {
      (function (cfg) {
        var wrap = el(cfg.wrap);
        var input = el(cfg.input);
        if (!wrap || !input) return;
        var dec = wrap.querySelector('.qi-num-dec');
        var inc = wrap.querySelector('.qi-num-inc');
        if (dec) dec.addEventListener('click', function () { stepField(cfg.input, cfg.wrap, -1); });
        if (inc) inc.addEventListener('click', function () { stepField(cfg.input, cfg.wrap, 1); });
        input.addEventListener('input', function () { syncCounter(cfg.input, cfg.wrap); });
        input.addEventListener('change', function () { syncCounter(cfg.input, cfg.wrap); });
        syncCounter(cfg.input, cfg.wrap);
      })(COUNTERS[i]);
    }

    // onImprintChange() toggles .disabled/.value from the inline onchange, which
    // always runs BEFORE listeners added here — so sync synchronously. Deferring
    // it left the buttons disabled for a tick after the imprint was chosen.
    var imp = el('imprintSelect');
    if (imp) imp.addEventListener('change', syncAllCounters);
  }

  /* ------------------------------------------------------------------- boot */

  function bind() {
    document.addEventListener('qi:price', function (e) { renderPrice(e.detail); });
    bindCounters();

    // qi-live-price.js may have already fired its first update before we bound
    // (both are `defer`, order is not guaranteed) — pull once to catch up.
    if (window.QILivePrice && typeof window.QILivePrice.update === 'function') {
      window.QILivePrice.update();
    }

    renderDelivery();
    // the date only changes across a midnight boundary; recheck hourly for
    // long-lived tabs so an overnight visitor is never quoted yesterday's date
    setInterval(renderDelivery, 3600000);
  }

  window.QICheckoutWins = {
    renderDelivery: renderDelivery,
    renderPrice: renderPrice,
    stepField: stepField,
    syncCounters: syncAllCounters
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind);
  } else {
    bind();
  }
})();
